import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import pick from 'lodash/pick';

import { RootState, AppThunkConfig } from '../../app/store';
import { ClientSummary, Client, ClientProvider, EntityRegisterOptions } from '../../types';
import { bindAPI } from '../../app/auth';
import { generatePath, route_path } from '../../app/route';
import {
  ListClientsResponse,
  listClients,
  CreateClientParams,
  CreateClientResponse,
  createClient,
  GetClientParams,
  GetClientResponse,
  getClient,
  UpdateClientParams,
  UpdateClientResponse,
  updateClient,
  DeleteClientParams,
  DeleteClientResponse,
  deleteClient,
} from '../../handlers';
import { logoutAction, hasScope } from '../auth/authSlice';
import {
  setCurrentTenantId,
  selectCurrentTenantId,
} from '../user/userSlice';
import { stripEmptyString } from '../../utils';

export type ClientState = {
  clients: ClientSummary[];
  list_status: 'idle'|'loading'|'failed'|'success';
  list_error?: SerializedError;
  get_status: 'idle'|'loading'|'failed'|'success';
  get_result?: ReturnType<typeof getClientAction.fulfilled>|ReturnType<typeof getClientAction.rejected>;
  create_status: 'idle'|'loading'|'failed'|'success';
  create_result?: ReturnType<typeof createClientAction.fulfilled>|ReturnType<typeof createClientAction.rejected>;
  update_status:  'idle'|'loading'|'failed'|'success';
  update_result?: ReturnType<typeof updateClientAction.fulfilled>|ReturnType<typeof updateClientAction.rejected>;
  delete_status:  'idle'|'loading'|'failed'|'success';
  delete_result?: ReturnType<typeof deleteClientAction.fulfilled>|ReturnType<typeof deleteClientAction.rejected>;
};

const initialState: ClientState = {
  clients: [],
  list_status: 'idle',
  get_status: 'idle',
  create_status: 'idle',
  update_status: 'idle',
  delete_status: 'idle',
};

export type ListClientsActionParams = {
  tenant_id: string;
};
export type ListClientsActionResponse = ListClientsResponse;
export const listClientsAction = createAsyncThunk<ListClientsResponse, ListClientsActionParams, AppThunkConfig>(
  'client/list',
  (params, thunkAPI) => bindAPI(listClients, thunkAPI)(params.tenant_id),
);

export type CreateClientActionParams = CreateClientParams;
export type CreateClientActionResponse = CreateClientResponse;
export const createClientAction = createAsyncThunk<CreateClientActionResponse, CreateClientActionParams, AppThunkConfig>(
  'client/create',
  (params, thunkAPI) => {
    let providers = params.body.providers.map(provider => stripEmptyString(provider));
    let new_body = pick(stripEmptyString(params.body), [
      'name', 'description', 'providers', 'full_role_id',
      'access_token', 'refresh_token', 'username',
      'is_published', 'state', 'authorized_redirect_uris',
    ]);
    return bindAPI(createClient, thunkAPI)({
      tenant_id: params.tenant_id,
      body: {
        ...new_body,
        providers,
      },
    });
  }
);

export type GetClientActionParams = GetClientParams;
export type GetClientActionResponse = GetClientResponse;
export const getClientAction = createAsyncThunk<GetClientActionResponse, GetClientActionParams, AppThunkConfig>(
  'client/get',
  (params, thunkAPI) => bindAPI(getClient, thunkAPI)(params)
);

export type UpdateClientActionParams = UpdateClientParams;
export type UpdateClientActionResponse = UpdateClientResponse;
export const updateClientAction = createAsyncThunk<UpdateClientActionResponse, UpdateClientActionParams, AppThunkConfig>(
  'client/update',
  (params, thunkAPI) => {
    let providers = params.body.providers?.map(provider => stripEmptyString(provider));
    let new_body = pick(stripEmptyString(params.body, { convert_to_null: true }), [
      'name', 'description', 'providers', 'full_role_id',
      'access_token', 'refresh_token', 'username',
      'is_published', 'state', 'authorized_redirect_uris',
    ]);
    return bindAPI(updateClient, thunkAPI)({
      tenant_id: params.tenant_id,
      client_name: params.client_name,
      body: {
        ...new_body,
        providers,
      },
    });
  }
);

export type DeleteClientActionParams = DeleteClientParams;
export type DeleteClientActionResponse = DeleteClientResponse;
export const deleteClientAction = createAsyncThunk<DeleteClientActionResponse, DeleteClientActionParams, AppThunkConfig>(
  'client/delete',
  (params, thunkAPI) => bindAPI(deleteClient, thunkAPI)(params)
);

export const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listClientsAction.pending, (state) => {
        state.list_status = 'loading';
      })
      .addCase(listClientsAction.fulfilled, (state, action) => {
        state.list_status = 'success';
        state.clients = action.payload.data;
      })
      .addCase(listClientsAction.rejected, (state, action) => {
        state.list_status = 'failed';
        state.list_error = action.error;
      })
      .addCase(getClientAction.pending, (state) => {
        state.get_status = 'loading';
      })
      .addCase(getClientAction.fulfilled, (state, action) => {
        state.get_status = 'idle';
        state.get_result = action;
      })
      .addCase(getClientAction.rejected, (state, action) => {
        state.get_status = 'failed';
        state.get_result = action;
      })
      .addCase(createClientAction.pending, (state) => {
        state.create_status = 'idle';
      })
      .addCase(createClientAction.fulfilled, (state, action) => {
        state.create_status = 'success';
        state.create_result = action;
      })
      .addCase(createClientAction.rejected, (state, action) => {
        state.create_status = 'failed';
        state.create_result = action;
      })
      .addCase(updateClientAction.pending, (state) => {
        state.update_status = 'idle';
      })
      .addCase(updateClientAction.fulfilled, (state, action) => {
        state.update_status = 'success';
        state.update_result = action;
      })
      .addCase(updateClientAction.rejected, (state, action) => {
        state.update_status = 'failed';
        state.update_result = action;
      })
      .addCase(deleteClientAction.pending, (state) => {
        state.delete_status = 'idle';
      })
      .addCase(deleteClientAction.fulfilled, (state, action) => {
        state.delete_status = 'success';
        state.delete_result = action;
      })
      .addCase(deleteClientAction.rejected, (state, action) => {
        state.delete_status = 'failed';
        state.delete_result = action;
      })
      .addCase(logoutAction, (state) => initialState)
      .addCase(setCurrentTenantId, (state) => initialState)
      .addCase(LOCATION_CHANGE, (state) => initialState);
  },
});

export const selectClients = (state: RootState) => state.client.clients;
export const selectClientAPIStatus = (state: RootState) => state.client.list_status;
export const selectCreateClientResult = (state: RootState) => state.client.create_result;
export const selectUpdateClientResult = (state: RootState) => state.client.update_result;
export const selectGetClientResult = (state: RootState) => state.client.get_result;

export const canActionByClient = (
  client: Pick<Client, 'state'>,
  action: 'edit'|'view'|'enable'|'disable'|'delete',
  tenant_id: string,
) => {
  if (action === 'edit') {
    return true;
  } else if (action === 'view') {
    return true;
  } else if (action === 'enable') {
    return client.state === 'disabled';
  } else if (action === 'disable') {
    return client.state === 'enabled';
  } else if (action === 'delete') {
    return true;
  }
  return false;
};

export const selectClientsAndActions = (state: RootState) => {
  const tenant_id = selectCurrentTenantId(state);
  const clients = selectClients(state);
  return clients.map(client => {
    return {
      ...client,
      $action: {
        can_edit: hasScope(state, 'auth-portal/user_clients.update') && canActionByClient(client, 'edit', tenant_id),
        can_view: hasScope(state, 'auth-portal/user_clients.get') && canActionByClient(client, 'view', tenant_id),
        can_enable: hasScope(state, 'auth-portal/user_clients.update') && canActionByClient(client, 'enable', tenant_id),
        can_disable: hasScope(state, 'auth-portal/user_clients.update') && canActionByClient(client, 'disable', tenant_id),
        can_delete: hasScope(state, 'auth-portal/user_clients.delete') && canActionByClient(client, 'delete', tenant_id),
      },
    };
  });
};
export const selectCanListClient = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_clients.list');
};

export const getClientListUrl = (tenant_id: string) => generatePath(route_path.client_list, { tenant_id });
export const getClientCreateUrl = (tenant_id: string) => generatePath(route_path.client_detail, {
  tenant_id, mode: 'create',
});
export const getClientDetailUrl = (tenant_id: string, client_name: string, mode: 'edit'|'view') => {
  return generatePath(route_path.client_detail, {
    tenant_id, mode,
  })+`?name=${client_name}`;
};

export const client_field_options: EntityRegisterOptions<Client> = {
  name: { 
    required: 'Required',
    minLength: {
      value: 2,
      message: 'Length must be at least 2 characters long',
    },
    maxLength: {
      value: 32,
      message: 'Length must be less than or equal to 32 characters long',
    },
    pattern: {
      value: /^[a-z][a-z-0-9]*$/,
      message: 'Fails to match the required pattern: /^[a-z][a-z-0-9]*$/',
    },
  },
  description: { 
    maxLength: {
      value: 2048,
      message: 'Length must be less than or equal to 2048 characters long',
    },
  },
  access_token: {
    expires_in: {
      min: {
        value: 300,
        message: 'Must be greater than 5 mins',
      },
      max: {
        value: 60*60*24*365,
        message: 'Must be less than 1 year',
      },
    },
  },
  refresh_token: {
    expires_in: {
      min: {
        value: 600,
        message: 'Must be greater than 10 mins',
      },
      max: {
        value: 60*60*24*365,
        message: 'Must be less than 1 year',
      },
    },
  },
};

export const client_provider_field_options: EntityRegisterOptions<ClientProvider> = {
  name: {
    required: 'Required a provider',
  },
};

export const client_authorized_redirect_uri_options: EntityRegisterOptions<{ id: string; name: string; }> = {
  name: {
    required: 'Required a provider',
  },
};
